import { Injectable } from '@angular/core';
import {
  ImpactReportData,
  CustomPieChartData,
} from '../models/impactReportData';
import { default as html2canvas } from 'html2canvas';
import jsPDF from 'jspdf';
import { GraphOptionKey } from '../models/graphOption';

@Injectable({
  providedIn: 'root',
})
export class PdfExportService {
  private hasInitialized = false;
  loading: boolean = false;
  MIN_DATA_LENGTH_FOR_CHART = 1;
  checked: boolean = true;
  isExportingToPDF = false;
  selectedCategories: GraphOptionKey[] = [];

  graphOptions: Record<GraphOptionKey, boolean> = {
    overview: true,
    overviewWithoutTransport: true,
    perTraveller: true,
    transportDetail: true,
    accommodationDetail: true,
    activityDetail: true,
    foodDetail: true,
    carbonDetail: true,
    annualDetail: true,
    comparisons: true,
  };

  graphKeys: GraphOptionKey[] = [
    'overview',
    'overviewWithoutTransport',
    'perTraveller',
    'transportDetail',
    'accommodationDetail',
    'activityDetail',
    'foodDetail',
    'carbonDetail',
    'annualDetail',
    'comparisons',
  ];

  graphCategories: {
    key: GraphOptionKey;
    name: string;
    icon: string;
    description: string;
  }[] = [
    {
      key: 'overview',
      name: 'global',
      icon: 'globe',
      description: 'overviewDetailsDescription',
    },
    {
      key: 'overviewWithoutTransport',
      name: 'globalWithoutTransport',
      icon: 'leaf',
      description: 'overviewWithoutTransportDetailsDescription',
    },
    {
      key: 'perTraveller',
      name: 'traveller',
      icon: 'user',
      description: 'travellerDetailsDescription',
    },
    {
      key: 'transportDetail',
      name: 'transportImpact',
      icon: 'car',
      description: 'transportDetails',
    },
    {
      key: 'accommodationDetail',
      name: 'accommodationImpact',
      icon: 'house',
      description: 'accommodationDetails',
    },
    {
      key: 'activityDetail',
      name: 'activityImpact',
      icon: 'cable-car',
      description: 'activityDetails',
    },
    {
      key: 'foodDetail',
      name: 'foodImpact',
      icon: 'apple',
      description: 'foodDetails',
    },
    {
      key: 'carbonDetail',
      name: 'carbonIntens',
      icon: 'gauge',
      description: 'carbonIntensity',
    },
    {
      key: 'annualDetail',
      name: 'goal',
      icon: 'target',
      description: 'annualBudget.title',
    },
    {
      key: 'comparisons',
      name: 'equivalentTitle',
      icon: 'list',
      description: 'equivalentDetails',
    },
  ];

  pdfOptions: Record<string, boolean> = {
    includeOverview: true,
    includeImpactPerTraveller: true,
    includeTransportDetails: true,
    includeAccommodationDetails: true,
    includeActivityDetails: true,
    includeFoodDetails: true,
    includeCarbonIntensity: true,
    includeAnnualBudget: true,
  };

  toggleAllCategories(): void {
    for (const key of this.graphKeys) {
      this.graphOptions[key] = this.checked;
    }

    if (this.checked) {
      this.selectedCategories = [...this.graphKeys].filter((key) =>
        this.graphCategories.some((cat) => cat.key === key),
      );
    } else {
      this.selectedCategories = [];
    }
  }

  onCategoryChange(): void {
    for (const cat of this.graphCategories) {
      this.graphOptions[cat.key] = this.selectedCategories.includes(cat.key);
    }
  }

  updateMainToggle(): void {
    const availableKeys = this.graphCategories.map((cat) => cat.key);
    this.checked = availableKeys.every((key) => this.graphOptions[key]);
  }

  toggleGraph(key: GraphOptionKey) {
    this.graphOptions[key] = !this.graphOptions[key];
    if (this.graphOptions[key]) {
      if (!this.selectedCategories.includes(key)) {
        this.selectedCategories.push(key);
      }
    } else {
      this.selectedCategories = this.selectedCategories.filter(
        (cat) => cat !== key,
      );
    }

    this.updateMainToggle();
  }

  get isGraphVisible() {
    return (key: GraphOptionKey) => this.graphOptions[key];
  }

  updateAvailableCategories(impactReportData: ImpactReportData | undefined): {
    graphCategories: {
      key: GraphOptionKey;
      name: string;
      icon: string;
      description: string;
    }[];
    selectedCategories: GraphOptionKey[];
  } | void {
    if (!impactReportData) return;

    const availableCategories: GraphOptionKey[] = [];

    if (impactReportData.overview) {
      availableCategories.push('overview');
    }

    if (impactReportData.overviewWithoutTransport) {
      availableCategories.push('overviewWithoutTransport');
    }

    if (impactReportData.overviewPerTraveller) {
      availableCategories.push('perTraveller');
    }

    if (this.displayPieChart(impactReportData.transportDetail)) {
      availableCategories.push('transportDetail');
    }

    if (this.displayPieChart(impactReportData.accommodationDetail)) {
      availableCategories.push('accommodationDetail');
    }

    if (this.displayPieChart(impactReportData.activityDetail)) {
      availableCategories.push('activityDetail');
    }

    if (this.displayPieChart(impactReportData.foodDetail)) {
      availableCategories.push('foodDetail');
    }

    if (impactReportData.carbonIntensity) {
      availableCategories.push('carbonDetail');
    }

    if (impactReportData.annualBudget !== undefined) {
      availableCategories.push('annualDetail');
    }

    if (impactReportData.overviewPerTraveller?.total) {
      availableCategories.push('comparisons');
    }

    this.graphCategories = this.graphCategories.filter((category) =>
      availableCategories.includes(category.key),
    );

    if (!this.hasInitialized) {
      this.graphKeys.forEach((key) => {
        this.graphOptions[key] = availableCategories.includes(key);
      });

      this.selectedCategories = availableCategories;

      this.updateMainToggle();

      this.hasInitialized = true;
    }
    this.selectedCategories = availableCategories;

    return {
      graphCategories: this.graphCategories,
      selectedCategories: this.selectedCategories,
    };
  }

  private addTripBasicInfo(
    pdf: jsPDF,
    impactReportData: ImpactReportData,
    currentY: number,
  ): number {
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const centerX = pdfWidth / 2;
    const lineHeight = 10;

    const title = impactReportData?.trip?.title || '-';
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(24);
    const titleWidth = pdf.getTextWidth(title);
    pdf.text(title, centerX - titleWidth / 2, currentY);
    currentY += lineHeight + 5;

    const travellers = ` ${impactReportData?.trip?.travellers ?? '-'}`;
    const days = ` ${impactReportData?.trip?.days ?? '-'}`;
    const country = ` ${impactReportData?.countryName || '-'}`;

    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(12);

    const spacing = 20;
    const travellersWidth = pdf.getTextWidth(travellers);
    const daysWidth = pdf.getTextWidth(days);
    const countryWidth = pdf.getTextWidth(country);

    const totalWidth =
      travellersWidth + spacing + daysWidth + spacing + countryWidth;
    let startX = centerX - totalWidth / 2;

    pdf.text(travellers, startX, currentY);
    startX += travellersWidth + spacing;

    pdf.text(days, startX, currentY);
    startX += daysWidth + spacing;

    pdf.text(country, startX, currentY);

    currentY += lineHeight;

    const adjustedY = currentY - 75;

    return adjustedY + 42;
  }

  async exportStyledPDF(
    impactReportData: ImpactReportData | undefined,
    dialogRef: { visible: boolean } | null = null,
  ): Promise<void> {
    this.isExportingToPDF = true;
    if (dialogRef) {
      dialogRef.visible = false;
    }
    this.loading = true;

    const content = document.getElementById('pdf-content');
    if (!content) {
      this.isExportingToPDF = false;
      return;
    }

    const pdf = new jsPDF('p', 'mm', 'a4');
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();
    const filename = `impactReport-${impactReportData?.trip.title}-${impactReportData?.trip.companyClients || ''}-${new Date().toLocaleDateString('en-GB').replace(/\//g, '-')}.pdf`;

    const loadFooterImage = (): Promise<string> => {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'Anonymous';
        img.onload = () => {
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = img.height;
          const ctx = canvas.getContext('2d');
          if (!ctx) return reject('Canvas context not available');
          ctx.drawImage(img, 0, 0);
          resolve(canvas.toDataURL('image/png'));
        };
        img.onerror = reject;
        img.src = '/assets/icons/blue-logo-bg.svg';
      });
    };
    const addFooter = (imgData: string) => {
      const footerText1 = 'Act4Travel';
      const footerText2 = ' tool by ';
      const footerText3 = 'GREENTRIPPER';

      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      const footerHeight = 15;
      const logoHeight = 22;
      const logoWidth = 34;
      const paddingX = 12;

      const logoY = pdfHeight - footerHeight + (footerHeight - logoHeight) / 2;
      const textY = pdfHeight - footerHeight / 2 + 2;

      pdf.setFillColor(241, 245, 249);
      pdf.rect(0, pdfHeight - footerHeight, pdfWidth, footerHeight, 'F');

      pdf.addImage(imgData, 'PNG', paddingX, logoY, logoWidth, logoHeight);

      const textX = paddingX + logoWidth - 8;
      pdf.setFontSize(14);
      pdf.setTextColor(21, 67, 93);
      pdf.setFont('helvetica', 'bold');
      pdf.text(footerText1, textX, textY);

      const footerText2X = textX + pdf.getTextWidth(footerText1);
      pdf.setFontSize(12);
      pdf.setTextColor(21, 67, 93);
      pdf.setFont('helvetica', 'normal');
      pdf.text(footerText2, footerText2X, textY);

      const footerText3X = footerText2X + pdf.getTextWidth(footerText2);
      pdf.setFontSize(14);
      pdf.setTextColor(21, 67, 93);
      pdf.setFont('helvetica', 'bold');
      pdf.text(footerText3, footerText3X, textY);
    };

    const footerImgData = await loadFooterImage();
    addFooter(footerImgData);

    const addHeaderContent = async (): Promise<number> => {
      const headerElement = document.querySelector(
        '.report-image',
      ) as HTMLElement;

      headerElement.style.transformOrigin = 'center center';
      headerElement.style.objectFit = 'cover';

      if (headerElement) {
        return new Promise<number>((resolve) => {
          html2canvas(headerElement, {
            scale: 1.5,
            backgroundColor: '#fff',
            scrollY: -window.scrollY,
          }).then((canvas) => {
            const imgData = canvas.toDataURL('image/jpeg', 1);
            const imgWidth = pdfWidth;
            const imgHeight = (canvas.height * imgWidth) / canvas.width;

            pdf.addImage(
              imgData,
              'JPEG',
              0,
              0,
              imgWidth,
              imgHeight,
              undefined,
              'FAST',
            );

            resolve(imgHeight);
          });
        });
      }

      return Promise.resolve(0);
    };

    const sections = content.querySelectorAll('.report-section');
    let currentY = 10;
    const horizontalMargin = 10;
    const adjustedWidth = pdfWidth - horizontalMargin * 2;

    const headerHeight = await addHeaderContent();
    currentY += headerHeight || 0;

    if (impactReportData) {
      currentY = this.addTripBasicInfo(pdf, impactReportData, currentY);
    }
    addFooter(footerImgData);

    const processSection = (section: Element): Promise<void> => {
      return new Promise<void>((resolve) => {
        html2canvas(section as HTMLElement, {
          scrollY: -window.scrollY,
          scale: 1.5,
          backgroundColor: '#fff',
        }).then((canvas) => {
          const imgData = canvas.toDataURL('image/jpeg', 0.7);
          const imgWidth = adjustedWidth;
          const imgHeight = (canvas.height * adjustedWidth) / canvas.width;

          const sectionHeight = imgHeight + 5;

          if (currentY + sectionHeight > pdfHeight - 20) {
            pdf.addPage();
            addFooter(footerImgData);
            currentY = 10;
          }

          pdf.addImage(
            imgData,
            'JPEG',
            horizontalMargin,
            currentY,
            imgWidth,
            imgHeight,
            undefined,
            'FAST',
          );
          currentY += sectionHeight;

          resolve();
        });
      });
    };

    const processSections = async (): Promise<void> => {
      if (sections.length === 0) {
        html2canvas(content, {
          scrollY: -window.scrollY,
          scale: 1.5,
          backgroundColor: '#fff',
        }).then((canvas) => {
          const imgData = canvas.toDataURL('image/jpeg', 0.7);
          const imgWidth = adjustedWidth;
          const imgHeight = (canvas.height * adjustedWidth) / canvas.width;

          let heightLeft = imgHeight;
          let position = currentY;

          pdf.addImage(
            imgData,
            'JPEG',
            horizontalMargin,
            position,
            imgWidth,
            imgHeight,
            undefined,
            'FAST',
          );
          heightLeft -= pdfHeight - position - 20;
          position = -(pdfHeight - position - 20);

          addFooter(footerImgData);

          while (heightLeft > 0) {
            pdf.addPage();
            pdf.addImage(
              imgData,
              'JPEG',
              horizontalMargin,
              position,
              imgWidth,
              imgHeight,
              undefined,
              'FAST',
            );
            heightLeft -= pdfHeight - 20;
            position -= pdfHeight - 20;

            addFooter(footerImgData);
          }

          pdf.save(filename);
          this.isExportingToPDF = false;
        });
        return;
      }

      for (let i = 0; i < sections.length; i++) {
        await processSection(sections[i]);
      }

      addFooter(footerImgData);

      pdf.save(filename);
      this.isExportingToPDF = false;
    };

    await processSections();
    this.loading = false;
  }

  displayPieChart(pieData: CustomPieChartData | undefined) {
    if (pieData === undefined) {
      return false;
    } else {
      return (
        pieData.chartData.labels &&
        pieData.chartData.labels?.length >= this.MIN_DATA_LENGTH_FOR_CHART
      );
    }
  }
}
